// useConfirmLoginStatus.js
import { useEffect } from 'react';

// Context
import { useUI } from 'context/UIContext';
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUser } from 'context/UserContext';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from 'firebaseConfig';

// Services
import { getUser, subscribeToUserNode } from 'services/users/UserService';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const USER_UNSET = Symbol('USER_UNSET');

let sharedUserBootstrap = null;

const isTransientUserBootstrapError = (error) => {
  const code = String(error?.code || '');
  return (
    code.includes('permission-denied')
    || code.includes('unavailable')
    || code.includes('deadline-exceeded')
  );
};

const loadUserWithRetry = async (uid, maxAttempts = 6, waitMs = 300) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const fetched = await getUser(uid);
      if (fetched) return fetched;
    } catch (error) {
      if (!isTransientUserBootstrapError(error) || attempt === maxAttempts) {
        throw error;
      }
    }

    if (attempt < maxAttempts) {
      // eslint-disable-next-line no-await-in-loop
      await delay(waitMs);
    }
  }

  return null;
};

const disposeSharedUserBootstrap = () => {
  if (!sharedUserBootstrap) return;

  sharedUserBootstrap.disposed = true;
  if (typeof sharedUserBootstrap.unsubscribe === 'function') {
    sharedUserBootstrap.unsubscribe();
  }

  sharedUserBootstrap.listeners.clear();
  sharedUserBootstrap = null;
};

const ensureSharedUserBootstrap = (uid) => {
  if (sharedUserBootstrap?.uid === uid && !sharedUserBootstrap.disposed) {
    return sharedUserBootstrap;
  }

  disposeSharedUserBootstrap();

  const entry = {
    uid,
    currentUser: USER_UNSET,
    disposed: false,
    listeners: new Set(),
    getDocFailed: false,
    snapshotFailed: false,
    errors: [],
    unsubscribe: null,
    readyPromise: null,
    resolveReady: null,
    rejectReady: null,
    settled: false,
  };

  const notifyListeners = (nextUser) => {
    entry.currentUser = nextUser;
    entry.listeners.forEach((listener) => {
      try {
        listener(nextUser);
      } catch (error) {
        console.error('[auth bootstrap] listener update error:', error);
      }
    });
  };

  const resolveReady = (payload) => {
    if (entry.disposed || entry.settled) return;
    entry.settled = true;
    entry.resolveReady(payload);
  };

  const rejectReady = (error) => {
    if (entry.disposed || entry.settled) return;
    entry.settled = true;
    entry.rejectReady(error);
  };

  entry.readyPromise = new Promise((resolve, reject) => {
    entry.resolveReady = resolve;
    entry.rejectReady = reject;
  });

  entry.subscribe = (listener) => {
    if (typeof listener !== 'function') {
      return () => {};
    }

    entry.listeners.add(listener);
    if (entry.currentUser !== USER_UNSET) {
      listener(entry.currentUser);
    }

    return () => {
      entry.listeners.delete(listener);
    };
  };

  entry.unsubscribe = subscribeToUserNode(
    uid,
    (nextUser) => {
      if (entry.disposed) return;
      notifyListeners(nextUser);
      resolveReady({
        user: nextUser,
        source: 'snapshot',
      });
    },
    (error) => {
      if (entry.disposed) return;

      if (entry.settled) {
        console.warn('[auth bootstrap] user snapshot error after initial resolution:', error);
        return;
      }

      entry.snapshotFailed = true;
      entry.errors.push(error);
      if (entry.getDocFailed) {
        rejectReady(entry.errors[0] || error);
      }
    }
  );

  loadUserWithRetry(uid)
    .then((nextUser) => {
      if (entry.disposed) return;
      notifyListeners(nextUser);
      resolveReady({
        user: nextUser,
        source: 'getDoc-race',
      });
    })
    .catch((error) => {
      if (entry.disposed || entry.settled) return;

      entry.getDocFailed = true;
      entry.errors.push(error);
      if (entry.snapshotFailed) {
        rejectReady(entry.errors[0] || error);
      }
    });

  sharedUserBootstrap = entry;
  return entry;
};

/**
 * useConfirmLoginStatus
 * 
 * Custom hook to detect a person's login status and set the value of
 * the user variable in the global context. If the person is logged
 * in, the user is fetched from the database and set. If the person
 * is not logged in, the user is set to null. This will allow the
 * system to detect whether a login modal should be displayed at any
 * given time, wherever the user is in the app.
 * 
 * The auth user will have a user UID, which will be identical
 * to the user key of the user we need to load into state.
 * 
 * Additionally, if the user is not logged in, this will display the
 * Login modal to force a login. This will ensure that a user is always
 * set in the global context before utilizing the app.
 */
const useConfirmLoginStatus = () => {

  // Global Context
  const { setLoginOpen } = useUI();
  const { clearStatus, setStatus } = useStartupStatus();

  // User Context
  const { setUser, setIsAuthLoading } = useUser();

  useEffect(() => {
    setStatus('startup.auth.session', {
      message: 'Checking your sign-in…',
      priority: 10,
    });
    setIsAuthLoading(true);
    let isMounted = true;

    // Firestore subscription for the current USER node, kept in sync
    // with authentication state. Ensures Stripe status and other
    // profile fields update in real time via onSnapshot.
    let userSnapshotUnsubscribe = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
      clearStatus('startup.auth.session');

      if (typeof userSnapshotUnsubscribe === 'function') {
        userSnapshotUnsubscribe();
        userSnapshotUnsubscribe = null;
      }

      if (!authUser && sharedUserBootstrap) {
        disposeSharedUserBootstrap();
      }

      if (authUser) {
        setStatus('startup.auth.profile', {
          message: 'Loading your profile…',
          priority: 20,
        });

        try {
          const bootstrap = ensureSharedUserBootstrap(authUser.uid);
          userSnapshotUnsubscribe = bootstrap.subscribe((nextUser) => {
            if (!isMounted) return;
            setUser(nextUser);
          });

          const { user: fetched } = await bootstrap.readyPromise;

          if (!isMounted) return;

          clearStatus('startup.auth.profile');
          setLoginOpen(!fetched);
        } catch (error) {
          if (!isMounted) return;
          clearStatus('startup.auth.profile');
          setUser(null);
          setLoginOpen(true);
        }
      } else {
        if (!isMounted) return;
        clearStatus('startup.auth.profile');
        setUser(null);
        setLoginOpen(false);
      }

      if (isMounted) {
        setIsAuthLoading(false);
      }
    });

    return () => {
      isMounted = false;
      clearStatus('startup.auth.session');
      clearStatus('startup.auth.profile');
      unsubscribeAuth();
      if (typeof userSnapshotUnsubscribe === 'function') {
        userSnapshotUnsubscribe();
      }
    };

  }, [clearStatus, setLoginOpen, setIsAuthLoading, setStatus, setUser]);

};

export default useConfirmLoginStatus;
